import { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Loader2, Mail, CheckCircle, Search, X, Ruler } from "lucide-react";
import { toast } from "sonner";
import Breadcrumbs from "@/components/Breadcrumbs";
import HockeyHeroBanner from "@/components/HockeyHeroBanner";
import HockeyReel from "@/components/HockeyReel";
import RugbyHeroBanner from "@/components/RugbyHeroBanner";
import SEOHead, { breadcrumbJsonLd } from "@/components/SEOHead";
import SizeGuideModal from "@/components/SizeGuideModal";

export default function Shop() {
  const { category } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isSpecials = searchParams.get("specials") === "true";

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(category || "");
  const [activeSub, setActiveSub] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [sortBy, setSortBy] = useState("relevance"); // relevance | price-asc | price-desc | newest
  const [sizeFilter, setSizeFilter] = useState("");
  const [showInStock, setShowInStock] = useState(false);
  const [showSizeGuide, setShowSizeGuide] = useState(false);

  useEffect(() => {
    // Legacy links: "Scrum Caps" is no longer a category — its products live under Rugby → Protection.
    if (category === "scrum-caps") {
      setActiveSub("Protection");
      navigate("/shop/rugby", { replace: true });
      return;
    }
    setActiveCategory(category || "");
  }, [category, navigate]);

  useEffect(() => {
    setLoading(true);
    let url = "/products";
    const params = new URLSearchParams();
    if (isSpecials) {
      params.set("is_special", "true");
    } else if (["rugby", "hockey", "netball", "training"].includes(activeCategory)) {
      params.set("category", activeCategory);
    }
    if (params.toString()) url += `?${params.toString()}`;
    api.get(url).then((r) => setProducts(r.data)).catch(() => {
      toast.error("Could not load products — please check your connection and try again");
    }).finally(() => setLoading(false));
  }, [activeCategory, isSpecials]);

  useEffect(() => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      setSearchResults(null);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const resp = await api.get(`/products/search?q=${encodeURIComponent(searchQuery.trim())}`);
        setSearchResults(resp.data);
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const clearSearch = () => { setSearchQuery(""); setSearchResults(null); };
  const rawList = searchResults !== null ? searchResults : products;
  // Dynamic subcategory buttons — only show subcategories that actually have products
  // in the current category (single source of truth = the loaded products).
  const availableSubs = (() => {
    if (searchResults !== null || !activeCategory) return [];
    const seen = [];
    rawList.forEach((p) => {
      const s = p.subcategory;
      if (s && !seen.includes(s)) seen.push(s);
    });
    if (seen.length === 0) return [];
    // Preferred display order; any others follow alphabetically.
    const ORDER = ["Shorts", "Shirts", "Dress", "Skort", "Tights", "Vests", "Shin Skins",
      "Netballs", "Protection", "Rugby Balls", "Kicking", "Equipment", "Training Equipment",
      "Cones & Markers", "Speed & Agility"];
    seen.sort((a, b) => {
      const ia = ORDER.indexOf(a), ib = ORDER.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
    return ["All", ...seen];
  })();
  // Products narrowed to the active subcategory (client-side, so filters stay context-aware)
  const subList = (activeSub && activeSub !== "All")
    ? rawList.filter((p) => p.subcategory === activeSub)
    : rawList;
  // Normalize size labels so "7-8", "7-8 Yrs", "7-8Yrs" match as one filter
  const normalizeSize = (s) => String(s).toLowerCase().replace(/\s+/g, "").replace(/yrs?$/, "");
  // Client-side filter + sort
  const displayProducts = (() => {
    let list = [...subList];
    if (sizeFilter) list = list.filter(p => Array.isArray(p.sizes) && p.sizes.some(s => normalizeSize(s) === normalizeSize(sizeFilter)));
    if (showInStock) list = list.filter(p => (p.stock || 0) > 0);
    if (sortBy === "price-asc") list.sort((a, b) => (a.price || 0) - (b.price || 0));
    else if (sortBy === "price-desc") list.sort((a, b) => (b.price || 0) - (a.price || 0));
    else if (sortBy === "newest") list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return list;
  })();

  // Aggregate available sizes across the CURRENT subcategory for the size filter chips
  // (deduped by normalized value — first-seen label is kept for display)
  const availableSizes = (() => {
    const seen = new Map();
    subList.flatMap(p => p.sizes || []).forEach(s => {
      const key = normalizeSize(s);
      if (!seen.has(key)) seen.set(key, s);
    });
    return Array.from(seen.values());
  })();

  // Context-aware size grouping — never mix apparel sizes with ball sizes / pack sizes
  const sizeGroups = (() => {
    const buckets = { "Clothing Size": [], "Ball Size": [], "Pack Size": [], "Type": [] };
    availableSizes.forEach((sz) => {
      const s = String(sz).trim();
      // Equipment/protection variant values (e.g. "S / Charcoal Black", "JNR / Hot Pink",
      // "L 59-60+CM") are NOT apparel clothing sizes — keep them out of the size filter.
      if (/\//.test(s) || /cm/i.test(s)) return;
      if (/pack/i.test(s)) buckets["Pack Size"].push(sz);
      else if (/\bsize\s*\d/i.test(s)) buckets["Ball Size"].push(sz);
      else if (/^(one size|set)$/i.test(s) || /^\d+\s*m$/i.test(s)) buckets["Type"].push(sz);
      else buckets["Clothing Size"].push(sz);
    });
    return Object.entries(buckets).filter(([, arr]) => arr.length > 0);
  })();
  const showSizeGroupLabels = sizeGroups.length > 1;

  const seoByCategory = {
    rugby: {
      title: "Rugby Gear South Africa — Shorts, Jerseys, Balls & Protection",
      description: "Shop rugby gear at Untamed Gear — sublimated rugby shorts and jerseys (locally made in South Africa), plus rugby balls, scrum caps and headgear, protection, mouthguards and kicking tees. No minimum order, nationwide PUDO delivery.",
      keywords: "rugby gear South Africa, rugby equipment South Africa, rugby shorts South Africa, rugby jerseys, rugby balls, scrum caps, rugby headgear, mouthguards, kicking tees",
    },
    hockey: {
      title: "Hockey Kit — Dresses, Skorts & Shin Skins",
      description: "Untamed Gear hockey — hockey dresses, skorts, tights, shirts and shin skins for women, girls and boys. Sublimated, breathable, made in SA.",
      keywords: "hockey dress, hockey skort, hockey tights, hockey shirts, field hockey kit South Africa",
    },
    netball: {
      title: "Netball Kit & Equipment — Dresses, Skorts, Tights & Netballs",
      description: "Untamed Gear netball — sublimated netball dresses, skorts, tights and vests, plus netballs and netball equipment. Made in South Africa. Colour-match your team kit.",
      keywords: "netball dress, netball skort, netball tights, netballs, netball equipment, netball uniforms South Africa",
    },
    "scrum-caps": {
      title: "Scrum Caps & Rugby Headgear",
      description: "Untamed Gear scrum caps for rugby. Padded protection, sizes XS to L, ventilated fit. Locally made in South Africa.",
      keywords: "scrum caps, rugby headgear, rugby head protection South Africa",
    },
    training: {
      title: "Training & Equipment — Agility, Cones & Markers",
      description: "Speed and agility ladders, training cones, field markers and Octa rings for rugby, netball and hockey training. Equipment from Untamed Gear South Africa.",
      keywords: "agility ladder South Africa, training cones South Africa, field markers, speed and agility equipment, coaching equipment",
    },
  };
  const activeSeo = seoByCategory[activeCategory] || {
    title: "Shop Sports Gear",
    description: "Shop the full Untamed Gear range — rugby shorts and jerseys, hockey dresses and skorts, netball kit, scrum caps and training wear. Bold sublimated sportswear made in South Africa.",
    keywords: "sports gear South Africa, rugby kit, hockey kit, netball kit, scrum caps, sportswear, Untamed Gear",
  };
  // Specials is a filtered view of /shop — canonicalise it to /shop (not separately indexable)
  const seoCanonical = isSpecials ? "/shop" : `/shop${activeCategory ? "/" + activeCategory : ""}`;
  const seoCrumbs = breadcrumbJsonLd([
    { name: "Home", path: "/" },
    { name: "Shop", path: "/shop" },
    ...(activeCategory ? [{ name: activeSeo.title.split("—")[0].trim(), path: `/shop/${activeCategory}` }] : []),
  ]);

  return (
    <div data-testid="shop-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <SEOHead
        title={isSpecials ? "Sale & Specials" : activeSeo.title}
        absoluteTitle={!isSpecials && !activeCategory ? "Shop Sports Gear | Untamed Gear South Africa" : undefined}
        description={activeSeo.description}
        keywords={activeSeo.keywords}
        canonicalPath={seoCanonical}
        jsonLd={seoCrumbs}
      />
      {/* Breadcrumbs */}
      <Breadcrumbs items={[
        { label: "Shop", href: "/shop" },
        ...(isSpecials
          ? [{ label: "Specials" }]
          : activeCategory === "rugby"
            ? [{ label: "Rugby Gear" }]
            : activeCategory === "hockey"
              ? [{ label: "Hockey Gear" }]
              : activeCategory === "netball"
                ? [{ label: "Netball Gear" }]
                : activeCategory === "training"
                  ? [{ label: "Training & Equipment" }]
                  : activeCategory === "scrum-caps"
                    ? [{ label: "Scrum Caps" }]
                    : [])
      ]} />

      {/* Header */}
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[#F7931E] text-sm font-bold tracking-[0.2em] uppercase mb-2 font-heading">
            {isSpecials ? "Hot Deals" : "Shop"}
          </p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter uppercase text-white font-heading">
            {isSpecials ? "Specials" : activeCategory === "rugby" ? "Rugby Gear" : activeCategory === "hockey" ? "Hockey Gear" : activeCategory === "netball" ? "Netball Gear" : activeCategory === "training" ? "Training & Equipment" : activeCategory === "scrum-caps" ? "Scrum Caps" : "Shop Sports Gear"}
          </h1>
          {!isSpecials && !activeCategory && (
            <p className="text-[#A3A3A3] text-sm md:text-lg mt-2 tracking-[0.15em] uppercase font-heading">Sportswear • Equipment • Performance</p>
          )}
        </div>
        {category && category !== "training" && !isSpecials && (
          <button
            type="button"
            data-testid="shop-size-guide-btn"
            onClick={() => setShowSizeGuide(true)}
            className="inline-flex items-center gap-2 border-2 border-[#F7931E] text-[#F7931E] font-bold uppercase tracking-wider text-xs px-4 py-2.5 hover:bg-[#F7931E] hover:text-black transition-colors btn-press"
          >
            <Ruler size={14} /> Size Guide & Fit Predictor
          </button>
        )}
      </div>

      {activeCategory === "rugby" && !isSpecials && (
        <p data-testid="rugby-category-intro" className="text-[#A3A3A3] text-sm sm:text-base leading-relaxed max-w-3xl -mt-4 mb-8">
          Everything for rugby in one place — Untamed Gear's locally made, sublimated rugby shorts and jerseys, plus rugby balls, scrum caps and headgear, protection and mouthguards, and kicking tees. No minimum order.
        </p>
      )}

      {category && category !== "training" && !isSpecials && (
        <SizeGuideModal
          open={showSizeGuide}
          onOpenChange={setShowSizeGuide}
          category={activeCategory === "scrum-caps" ? "scrum-caps" : activeCategory}
        />
      )}

      {/* Hockey hero banner — auto-rotating action shots */}
      {activeCategory === "hockey" && !isSpecials && <HockeyHeroBanner />}

      {/* Rugby hero banner — team group shot */}
      {activeCategory === "rugby" && !isSpecials && <RugbyHeroBanner />}

      {/* Hockey reel — full length promo video, sits alongside the hero on desktop */}
      {activeCategory === "hockey" && !isSpecials && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10 items-stretch">
          <div className="lg:col-span-2 bg-[#171717] border border-[#262626] p-6 sm:p-8 flex flex-col justify-center">
            <p className="text-[#F7931E] text-xs font-bold uppercase tracking-[0.3em] mb-3 font-heading">
              Reel
            </p>
            <h3 className="text-white text-2xl sm:text-3xl font-black uppercase tracking-tighter leading-tight mb-4 font-heading">
              Untamed Gear on the Astro
            </h3>
            <p className="text-[#A3A3A3] text-sm sm:text-base leading-relaxed">
              Real players. Real matches. Real energy. Watch the girls light up the field in
              the full Untamed Gear hockey kit — sublimated shirts, shorts and tights built
              to move fast and stay bold. Tap the reel to play with sound.
            </p>
          </div>
          <div className="aspect-[9/16] sm:aspect-[9/16] lg:aspect-auto lg:h-full max-h-[560px] mx-auto w-full max-w-[300px] lg:max-w-none">
            <HockeyReel
              src="/api/uploads/videos/hockey_reel.mp4"
              poster="/api/uploads/videos/hockey_reel_poster.webp"
              className="w-full h-full"
            />
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#525252]" />
          <input
            data-testid="product-search-input"
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search products..."
            className="w-full bg-[#171717] border border-[#262626] text-white pl-10 pr-10 py-2.5 text-sm outline-none focus:border-[#F7931E] transition-colors placeholder:text-[#525252]"
          />
          {searchQuery && (
            <button data-testid="clear-search-btn" onClick={clearSearch} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#525252] hover:text-white transition-colors">
              <X size={16} />
            </button>
          )}
          {searching && <Loader2 size={16} className="absolute right-10 top-1/2 -translate-y-1/2 animate-spin text-[#F7931E]" />}
        </div>
        {searchResults !== null && (
          <p className="text-[#A3A3A3] text-xs mt-2">
            {searchResults.length} result{searchResults.length !== 1 ? "s" : ""} for "{searchQuery}"
            <button onClick={clearSearch} className="text-[#F7931E] ml-2 hover:underline">Clear</button>
          </p>
        )}
      </div>

      {/* Category Tabs */}
      {!isSpecials && searchResults === null && (
        <div className="flex flex-wrap gap-3 mb-6">
          <button
            data-testid="tab-all"
            onClick={() => { setActiveCategory(""); setActiveSub("All"); setSizeFilter(""); }}
            className={`font-bold uppercase tracking-wider text-sm px-5 py-2 transition-colors btn-press ${
              activeCategory === "" ? "bg-[#F7931E] text-black" : "border border-[#525252] text-white hover:bg-[#171717]"
            }`}>
            All Sports
          </button>
          <button
            data-testid="tab-rugby"
            onClick={() => { setActiveCategory("rugby"); setActiveSub("All"); }}
            className={`font-bold uppercase tracking-wider text-sm px-5 py-2 transition-colors btn-press ${
              activeCategory === "rugby" ? "bg-[#F7931E] text-black" : "border border-[#525252] text-white hover:bg-[#171717]"
            }`}>
            Rugby
          </button>
          <button data-testid="tab-hockey"
            onClick={() => { setActiveCategory("hockey"); setActiveSub("All"); }}
            className={`font-bold uppercase tracking-wider text-sm px-5 py-2 transition-colors btn-press ${
              activeCategory === "hockey" ? "bg-[#F7931E] text-black" : "border border-[#525252] text-white hover:bg-[#171717]"
            }`}>
            Hockey
          </button>
          <button data-testid="tab-netball"
            onClick={() => { setActiveCategory("netball"); setActiveSub("All"); }}
            className={`font-bold uppercase tracking-wider text-sm px-5 py-2 transition-colors btn-press ${
              activeCategory === "netball" ? "bg-[#F7931E] text-black" : "border border-[#525252] text-white hover:bg-[#171717]"
            }`}>
            Netball
          </button>
          <button data-testid="tab-training"
            onClick={() => { setActiveCategory("training"); setActiveSub("All"); }}
            className={`font-bold uppercase tracking-wider text-sm px-5 py-2 transition-colors btn-press ${
              activeCategory === "training" ? "bg-[#F7931E] text-black" : "border border-[#525252] text-white hover:bg-[#171717]"
            }`}>
            Training
          </button>
        </div>
      )}

      {/* Subcategory Filter — dynamic: only subcategories that have products */}
      {!isSpecials && ["rugby", "hockey", "netball", "training"].includes(activeCategory) && searchResults === null && availableSubs.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-8">
          {availableSubs.map((sub) => (
            <button key={sub} data-testid={`filter-${sub.toLowerCase().replace(/\s/g, '-')}`}
              onClick={() => { setActiveSub(sub); setSizeFilter(""); }}
              className={`text-xs font-bold uppercase tracking-wider px-4 py-1.5 transition-colors ${
                activeSub === sub
                  ? "bg-white text-black"
                  : "text-[#A3A3A3] hover:text-white border border-[#262626] hover:border-[#525252]"
              }`}>
              {sub}
            </button>
          ))}
        </div>
      )}

      {/* Sort + Size + Stock Filter Bar */}
      {!loading && rawList.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-6 pb-4 border-b border-[#262626]">
          <span className="text-[#525252] text-xs uppercase tracking-widest font-bold">Filter:</span>
          <select
            data-testid="filter-sort"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-[#171717] border border-[#262626] text-white text-xs px-3 py-1.5 focus:border-[#F7931E] outline-none uppercase tracking-wider font-bold"
          >
            <option value="relevance">Sort: Relevance</option>
            <option value="price-asc">Price: Low → High</option>
            <option value="price-desc">Price: High → Low</option>
            <option value="newest">Newest First</option>
          </select>
          {activeCategory !== "" && availableSizes.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <button
                data-testid="size-filter-all"
                onClick={() => setSizeFilter("")}
                className={`text-xs font-bold uppercase tracking-wider px-2.5 py-1 transition-colors ${
                  sizeFilter === "" ? "bg-[#F7931E] text-black" : "text-[#A3A3A3] hover:text-white border border-[#262626]"
                }`}
              >All Sizes</button>
              {sizeGroups.map(([label, sizes]) => (
                <div key={label} className="flex flex-wrap items-center gap-1">
                  {showSizeGroupLabels && (
                    <span className="text-[#525252] text-[10px] uppercase tracking-widest font-bold mr-0.5">{label}:</span>
                  )}
                  {sizes.map((sz) => (
                    <button
                      key={sz}
                      data-testid={`size-filter-${sz.toLowerCase().replace(/\s/g, '-')}`}
                      onClick={() => setSizeFilter(sz)}
                      className={`text-xs font-bold uppercase tracking-wider px-2.5 py-1 transition-colors ${
                        sizeFilter === sz ? "bg-[#F7931E] text-black" : "text-[#A3A3A3] hover:text-white border border-[#262626]"
                      }`}
                    >{sz}</button>
                  ))}
                </div>
              ))}
            </div>
          )}
          <label className="inline-flex items-center gap-2 cursor-pointer ml-auto">
            <input
              data-testid="filter-instock"
              type="checkbox"
              checked={showInStock}
              onChange={(e) => setShowInStock(e.target.checked)}
              className="w-4 h-4 accent-[#F7931E] cursor-pointer"
            />
            <span className="text-[#A3A3A3] text-xs uppercase tracking-wider font-bold">In Stock Only</span>
          </label>
        </div>
      )}

      {/* Products */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="animate-spin text-[#F7931E]" size={32} />
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-24">
          <p className="text-[#A3A3A3] text-lg">No products found.</p>
        </div>
      ) : activeCategory === "hockey" && !isSpecials ? (
        // Hockey: split by gender — Girls / Women (top) then Men / Boys (bottom)
        (() => {
          const girls = displayProducts.filter((p) => (p.gender || "womens") === "womens");
          const mens = displayProducts.filter((p) => p.gender === "mens");
          return (
            <div className="space-y-14">
              {girls.length > 0 && (
                <section data-testid="hockey-girls-section">
                  <div className="flex items-baseline gap-4 mb-6 pb-3 border-b border-[#262626]">
                    <h2 className="text-2xl sm:text-3xl font-black tracking-tighter uppercase text-white font-heading">Girls / Women Hockey</h2>
                    <span className="text-[#525252] text-xs uppercase tracking-widest font-bold">{girls.length} item{girls.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 stagger-children">
                    {girls.map((p) => (
                      <div key={p.id} className="animate-fade-in-up opacity-0">
                        <ProductCard product={p} viewCategory={activeCategory} />
                      </div>
                    ))}
                  </div>
                </section>
              )}
              {mens.length > 0 && (
                <section data-testid="hockey-mens-section">
                  <div className="flex items-baseline gap-4 mb-6 pb-3 border-b border-[#262626]">
                    <h2 className="text-2xl sm:text-3xl font-black tracking-tighter uppercase text-white font-heading">Mens / Boys Hockey</h2>
                    <span className="text-[#525252] text-xs uppercase tracking-widest font-bold">{mens.length} item{mens.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 stagger-children">
                    {mens.map((p) => (
                      <div key={p.id} className="animate-fade-in-up opacity-0">
                        <ProductCard product={p} viewCategory={activeCategory} />
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          );
        })()
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 stagger-children">
          {displayProducts.map((p) => (
            <div key={p.id} className="animate-fade-in-up opacity-0">
              <ProductCard product={p} viewCategory={activeCategory} />
            </div>
          ))}
        </div>
      )}

      {/* Product Count */}
      {!loading && displayProducts.length > 0 && (
        <p className="text-[#525252] text-sm mt-8">{displayProducts.length} product{displayProducts.length !== 1 ? 's' : ''}</p>
      )}

      {/* Newsletter Signup — show on all shop pages */}
      <NewsletterSignupForm />
    </div>
  );
}

function NewsletterSignupForm() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Please enter your email"); return; }
    if (!consent) { toast.error("Please tick the consent box to subscribe"); return; }
    setSubmitting(true);
    try {
      const resp = await api.post("/newsletter/subscribe", { email: email.trim(), name: name.trim(), marketing_consent: true });
      setSigned(true);
      toast.success(resp.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not sign up. Try again.");
    } finally { setSubmitting(false); }
  };

  if (signed) {
    return (
      <div data-testid="newsletter-success" className="mt-16 border-t border-[#262626] pt-12 pb-8 text-center">
        <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
        <h3 className="text-2xl font-black tracking-tighter uppercase text-white font-heading mb-2">You're In!</h3>
        <p className="text-[#A3A3A3]">We'll let you know when new specials drop.</p>
      </div>
    );
  }

  return (
    <div data-testid="newsletter-signup" className="mt-16 border-t border-[#262626] pt-12 pb-8">
      <div className="max-w-xl mx-auto text-center">
        <div className="w-14 h-14 bg-[#F7931E]/10 border border-[#F7931E]/30 flex items-center justify-center mx-auto mb-4">
          <Mail size={24} className="text-[#F7931E]" />
        </div>
        <h3 className="text-2xl sm:text-3xl font-black tracking-tighter uppercase text-white font-heading mb-2">Don't Miss Out</h3>
        <p className="text-[#A3A3A3] text-sm mb-6">Sign up to get notified about future specials, exclusive deals, and new gear drops.</p>
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
          <input data-testid="newsletter-name" type="text" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Your name (optional)"
            className="bg-black border border-[#262626] text-white px-4 py-3 text-sm outline-none focus:border-[#F7931E] transition-colors sm:w-40" />
          <input data-testid="newsletter-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            className="bg-black border border-[#262626] text-white px-4 py-3 text-sm outline-none focus:border-[#F7931E] transition-colors flex-1" required />
          <button data-testid="newsletter-submit-btn" type="submit" disabled={submitting || !consent}
            className="bg-[#F7931E] text-black font-bold uppercase tracking-wide text-sm px-8 py-3 hover:bg-[#D97715] transition-colors btn-press disabled:opacity-50 whitespace-nowrap">
            {submitting ? "Signing up..." : "Sign Up"}
          </button>
        </form>
        <label className="flex items-start gap-2 cursor-pointer mt-4 max-w-md mx-auto text-left">
          <input
            data-testid="newsletter-consent"
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-[#F7931E] cursor-pointer flex-shrink-0"
          />
          <span className="text-[#A3A3A3] text-xs leading-snug">
            I agree to receive marketing emails from Untamed Gear and accept the{" "}
            <a href="/policies" target="_blank" rel="noopener noreferrer" className="text-[#F7931E] underline">Privacy Policy (POPIA)</a>. Unsubscribe anytime.
          </span>
        </label>
        <p className="text-[#525252] text-xs mt-3">No spam. Unsubscribe anytime.</p>
      </div>
    </div>
  );
}
